import { Box, IconButton } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import { CircleChevronDown, CircleChevronUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface ExpandableTextProps {
    initialHeight?: number;
    children: JSX.Element | undefined;
    extraHiddenChildren?: JSX.Element;
}

/**
 * Function ExpandableText
 *
 * @param {ExpandableTextProps} props - The props for the ExpandableText component.
 * @returns {JSX.Element} - The JSX element representing the ExpandableText component.
 */
function ExpandableText(props: ExpandableTextProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).layout;

    const [small, setSmall] = useState<boolean>(true);
    const [height, setHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!contentRef.current) return;
        setHeight(contentRef.current.scrollHeight);
    }, [contentRef, small]);

    useEffect(() => {
        const handleResize = () => {
            if (contentRef.current) {
                setHeight(contentRef.current.scrollHeight);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const needsExpansion = height > (props.initialHeight || 65);

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    overflow: 'hidden',
                    maxHeight: small ? `${props.initialHeight || 65}px` : 'none',
                    transition: 'max-height 0.3s ease',
                    cursor: needsExpansion ? 'pointer' : 'default',
                    ...(small && {
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 3,
                        textOverflow: 'ellipsis',
                        lineHeight: '1.4em',
                    }),
                }}
                onClick={needsExpansion ? () => setSmall(!small) : undefined}
            >
                <Box
                    ref={contentRef}
                    sx={{
                        minHeight: '20px',
                        cursor: 'default',
                    }}
                    title={needsExpansion ? langStrings.expandableText : undefined}
                >
                    {props.children ? props.children : <>{langStrings.expandableText}</>}
                    {!small && props.extraHiddenChildren}
                </Box>
            </Box>
            {needsExpansion && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 1,
                    }}
                >
                    <IconButton
                        size="medium"
                        onClick={() => setSmall(!small)}
                        sx={{
                            p: 0.5,
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'primary.main',
                                bgcolor: 'action.hover',
                            },
                        }}
                        title={small ? 'Erweitern' : 'Einklappen'}
                    >
                        {small ? (
                            <CircleChevronDown size={24} strokeWidth={1.25} />
                        ) : (
                            <CircleChevronUp size={24} strokeWidth={1.25} />
                        )}
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}

export default ExpandableText;
